/**
 * useStepNavigation Hook
 * Reads entity type from Redux, resolves steps, provides navigation helpers.
 */

import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import type { RootState } from '@/store';
import type { StepId } from '@/types/onboarding';
import { getStepConfig } from '@/config/stepConfigs';
import { saveCurrentStep } from '@/services/firebase/vendorFirestoreService';

export function useStepNavigation() {
  const entityType = useSelector((s: RootState) => s.entity.entityType);
  const vendorId = useSelector((s: RootState) => s.auth.vendorId);
  const navigate = useNavigate();
  const location = useLocation();

  const steps = useMemo(() => getStepConfig(entityType), [entityType]);

  const currentStepIndex = useMemo(() => {
    const idx = steps.findIndex((s) => s.path === location.pathname);
    return idx >= 0 ? idx : 0;
  }, [steps, location.pathname]);

  const currentStep = steps[currentStepIndex] ?? steps[0];
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === steps.length - 1;
  const totalSteps = steps.length;

  const next = useCallback(() => {
    if (!isLast) {
      const nextStep = steps[currentStepIndex + 1];
      if (vendorId) {
        saveCurrentStep(vendorId, nextStep.id);
      }
      navigate(nextStep.path);
    }
  }, [isLast, navigate, steps, currentStepIndex, vendorId]);

  const prev = useCallback(() => {
    if (!isFirst) {
      navigate(steps[currentStepIndex - 1].path);
    }
  }, [isFirst, navigate, steps, currentStepIndex]);

  const goTo = useCallback(
    (stepId: StepId) => {
      const step = steps.find((s) => s.id === stepId);
      if (step) navigate(step.path);
    },
    [steps, navigate],
  );

  const goToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < steps.length) {
        navigate(steps[index].path);
      }
    },
    [steps, navigate],
  );

  return {
    steps,
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirst,
    isLast,
    next,
    prev,
    goTo,
    goToIndex,
    entityType,
  };
}
